import { Dropdown } from '@/components/Dropdown/Dropdown';
import { ListItemText, RedListItemText } from '@/components/Dropdown/MenuItems/components';
import { RawDataMenuItem, shouldShowRawMenuItem } from '@/components/Dropdown/MenuItems/RawDataMenuItem';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { ShareMenuContent } from '@/components/ShareMenu/ShareMenu';
import { ThreeDotsCircle } from '@/components/ThreeDotsCircle';
import stars from '@/images/agents/stars.svg';
import CodeIcon from '@/images/Code.svg';
import CopyLink from '@/images/CopyLink.svg';
import Edit from '@/images/Edit.svg';
import LiteFilled from '@/images/LiteFilled.svg';
import Share from '@/images/Share.svg';
import Unlink from '@/images/Unlink.svg';
import { useAuth } from '@/services/auth';
import theme from '@/theme';
import { copyToClipboardAsShareUrl, createShareUrl, sendShareMixpanelEvent } from '@/utils/shareUtils';
import { AgentConvertStatus, AgentStatus, IAgent } from '@d-id/types';
import styled from '@emotion/styled';
import { MenuList, ListItemIcon as MuiListItemIcon, MenuItem as MuiMenuItem, useTheme } from '@mui/material';
import { useState } from 'react';
import { atom, useRecoilState } from 'recoil';
import Remove from '../../../images/Remove.svg';
import ShareMenuItem from './ShareMenuItem';

export const currentAgentOptionList = atom<{ agent: IAgent } | null>({
    default: null,
    key: 'current-agent-option-list',
});

const MenuItem = styled(MuiMenuItem)`
    border-radius: 8px;
    padding: 1rem;
`;

const ListItemIcon = styled(MuiListItemIcon)`
    min-width: 3.2rem;
`;

const BorderLine = styled.div`
    border-top: 1px solid ${({ theme }) => theme.colors.settings};
    margin: 8px 0 14px 0;
`;

const StyledList = styled(MenuList)`
    padding: 0;

    &:focus {
        outline: none;
    }
`;

const OptionsButtonWrapper = styled.div`
    height: 4rem;
    width: 4rem;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
`;

const StyledNew = styled('span')`
    background: ${theme.colors.primary.bgLight};
    color: ${theme.colors.primary.main};
    display: flex;
    padding: 2px 3px;
    justify-content: center;
    align-items: center;
    gap: 8.889px;
    border-radius: 3.5px;
    font-style: normal;
    font-family: Manrope;
    font-size: 12px;
    margin-left: 8px;
    font-weight: 700;
    letter-spacing: -0.24px;
`;

interface Props {
    onDelete: (agent: IAgent) => void;
    onEmbed: (agent: IAgent) => void;
    onRemoveEmbed: (agent: IAgent) => void;
    onEdit: (agent: IAgent) => void;
    clientKey?: string;
    onOpen?: () => void;
    onClose?: () => void;
    onMigrate?: (agent: IAgent) => void;
    showMigration?: boolean;
}

export const AgentOptionsList = ({
    clientKey,
    onDelete,
    onEmbed,
    onRemoveEmbed,
    onOpen,
    onClose,
    onEdit,
    showMigration,
    onMigrate,
}: Props) => {
    const { user } = useAuth();
    const [currentOptionListData, _] = useRecoilState(currentAgentOptionList);
    const [closed, setClosed] = useState<boolean>(true);
    const theme = useTheme();

    const handleCloseMenu = () => {
        setClosed(true);
        onClose?.();
    };

    const handleOpenMenu = () => {
        setClosed(false);
        onOpen?.();
    };

    const handleShare = (option: string) => {
        sendShareMixpanelEvent({
            to: option,
            modal: 'play',
            item: { id: currentOptionListData?.agent.id || '', type: 'agent' },
        });

        handleCloseMenu();
    };

    const canStartConversation = currentOptionListData?.agent.status === AgentStatus.Done;
    const isAgentDraft = currentOptionListData?.agent.status === AgentStatus.Created;
    const isInProgress =
        currentOptionListData?.agent.status === AgentStatus.Started || currentOptionListData?.agent.status === AgentStatus.Ready;
    const items = [];

    if (showMigration && currentOptionListData?.agent && onMigrate && canStartConversation) {
        items.push({
            label: (
                <MenuItem
                    disabled={currentOptionListData?.agent?.conversion !== AgentConvertStatus.V2Convertable}
                    onClick={() => {
                        if (currentOptionListData?.agent) {
                            onMigrate?.(currentOptionListData.agent);
                            handleCloseMenu();
                        }
                    }}>
                    <ListItemIcon>
                        <MaskedIcon src={stars} size="24px" color="black" />
                    </ListItemIcon>
                    <ListItemText>Upgrade to Agent 2.0</ListItemText>
                    <StyledNew>NEW</StyledNew>
                </MenuItem>
            ),
        });
    }
    if (canStartConversation || isAgentDraft) {
        items.push(
            {
                label: (
                    <MenuItem
                        disabled={isAgentDraft}
                        onClick={() => {
                            copyToClipboardAsShareUrl({
                                itemId: currentOptionListData?.agent.id || '',
                                type: 'agent',
                                clientKey,
                            });
                            handleCloseMenu();
                        }}>
                        <ListItemIcon>
                            <img src={CopyLink} />
                        </ListItemIcon>
                        <ListItemText>Copy link</ListItemText>
                    </MenuItem>
                ),
            },
            {
                label: (
                    <ShareMenuItem
                        title={currentOptionListData?.agent.embed ? 'Manage embed' : 'Embed'}
                        onClick={() => {
                            onEmbed(currentOptionListData?.agent);
                            handleCloseMenu();
                        }}
                        disabled={isAgentDraft}
                        shareTooltip={''}
                        icon={CodeIcon}
                        hasRightArrow={false}
                        endIcon={user?.isTrial && LiteFilled}
                    />
                ),
            }
        );

        items.push({
            label: <ShareMenuItem title="Share" disabled={isAgentDraft} shareTooltip={''} icon={Share} hasRightArrow={true} />,
            items: [
                {
                    label: (
                        <ShareMenuContent
                            createShareUrl={(option: string) =>
                                createShareUrl({
                                    itemId: currentOptionListData?.agent.id || '',
                                    from: option,
                                    type: 'agent',
                                    clientKey,
                                })
                            }
                            handleShare={handleShare}
                        />
                    ),
                },
            ],
        });
    }
    if (!!currentOptionListData?.agent) {
        items.push({
            label: (
                <MenuItem
                    onClick={() => {
                        onEdit(currentOptionListData?.agent);
                        handleCloseMenu();
                    }}
                    disabled={isInProgress}>
                    <ListItemIcon>
                        <MaskedIcon src={Edit} size="22px" margin="0 0 0 3px" color={theme.colors.black.main} />
                    </ListItemIcon>
                    <ListItemText>Edit</ListItemText>
                </MenuItem>
            ),
        });
    }
    items.push({
        label: <BorderLine theme={theme} />,
    });

    if (currentOptionListData?.agent.embed) {
        items.push({
            label: (
                <MenuItem
                    onClick={() => {
                        onRemoveEmbed(currentOptionListData?.agent);
                        handleCloseMenu();
                    }}>
                    <ListItemIcon>
                        <MaskedIcon src={Unlink} size="24px" color={theme.colors.black.main} />
                    </ListItemIcon>
                    <ListItemText>Remove embed</ListItemText>
                </MenuItem>
            ),
        });
    }

    items.push({
        label: (
            <MenuItem
                onClick={() => {
                    if (!!currentOptionListData) {
                        onDelete(currentOptionListData?.agent);
                        handleCloseMenu();
                    }
                }}>
                <ListItemIcon>
                    <MaskedIcon src={Remove} size="24px" color={theme.colors.error[750]} />
                </ListItemIcon>
                <RedListItemText>Delete</RedListItemText>
            </MenuItem>
        ),
    });

    if (shouldShowRawMenuItem(user)) {
        items.push({
            label: <RawDataMenuItem dataToShow={currentOptionListData} closeMenu={handleCloseMenu} />,
        });
    }

    return (
        <Dropdown
            closed={closed}
            onOpen={handleOpenMenu}
            onClose={handleCloseMenu}
            trigger={
                <OptionsButtonWrapper>
                    <ThreeDotsCircle isActive={!closed} />
                </OptionsButtonWrapper>
            }
            items={items}
            renderMenuWrapper={children => <StyledList onClick={e => e.stopPropagation()}>{children}</StyledList>}
        />
    );
};
